package com.bikompanion.data.rating.firebase

import dev.gitlive.firebase.database.DataSnapshot
import dev.gitlive.firebase.database.FirebaseDatabase
import dev.zacsweers.metro.ContributesBinding
import dev.zacsweers.metro.Inject
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.withTimeout
import com.bikompanion.data.cache.LocalStore
import com.bikompanion.data.cache.cacheKey
import com.bikompanion.data.logRatingDiagnostic
import com.bikompanion.data.rating.PendingVote
import com.bikompanion.data.rating.PendingVoteQueue
import com.bikompanion.domain.AnonymousIdentityProvider
import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeKey
import com.bikompanion.domain.BikeRatingRepository
import com.bikompanion.domain.BikeRatingScore
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.BikeVoteFailure
import com.bikompanion.domain.PendingVoteFlushResult
import com.bikompanion.domain.SubmitVoteResult
import com.bikompanion.domain.di.AppScope
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlin.time.ExperimentalTime

/** A single vote can't land closer than this to the same identity's last one on the same bike. */
private const val CooldownMs = 5 * 60 * 1000L

/** How many monthly buckets make up a score — long enough to survive a quiet winter, short enough to forget an old fault. */
private const val ScoreWindowMonths = 6

/** Past this a vote goes to the offline queue rather than leaving the rider watching a spinner. */
private val WriteTimeoutMs = 8.seconds

/**
 * Bike votes in the Firebase Realtime Database.
 *
 * One vote is one atomic multi-path write (see [VoteUpdateBuilder]) and one score is one read of
 * a small aggregate node — no query, no index, and a missing node simply means nobody has voted.
 *
 * The cooldown is checked twice on purpose. Here, against a locally cached timestamp, it costs no
 * round trip and the rider gets an answer immediately; in the security rules it is the actual
 * enforcement, because nothing on the device can be trusted to have run.
 */
@ContributesBinding(AppScope::class)
@Inject
class BikeRatingRepositoryImpl(
    private val database: FirebaseDatabase,
    private val identity: AnonymousIdentityProvider,
    private val updateBuilder: VoteUpdateBuilder,
    private val pendingVotes: PendingVoteQueue,
    private val store: LocalStore,
) : BikeRatingRepository {
    private val flushMutex = Mutex()

    override suspend fun submitVote(
        key: BikeKey,
        isPositive: Boolean,
        isElectric: Boolean,
        issues: List<BikeIssue>,
        scopeSource: BikeScopeSource,
    ): SubmitVoteResult {
        if (!RatingPaths.isValid(key)) {
            return SubmitVoteResult.Failed(BikeVoteFailure.InvalidBike)
        }
        val now = Clock.System.now().toEpochMilliseconds()
        val lastVoteAt = lastLocalVoteAt(key)
        if (lastVoteAt != null && now - lastVoteAt in 0 until CooldownMs) {
            return SubmitVoteResult.InCooldown(retryAfterMs = CooldownMs - (now - lastVoteAt))
        }

        val vote = PendingVote(
            networkId = key.networkId,
            bikeNumber = key.bikeNumber,
            isPositive = isPositive,
            isElectric = isElectric,
            issues = issues,
            scopeSource = scopeSource,
            castAtMs = now,
        )
        return when (val outcome = send(vote)) {
            SendOutcome.Sent -> {
                rememberLocalVote(key, now)
                flushPending()
                SubmitVoteResult.Recorded
            }

            is SendOutcome.Rejected -> {
                SubmitVoteResult.Failed(BikeVoteFailure.Rejected)
            }

            SendOutcome.Unreachable -> {
                pendingVotes.enqueue(vote)
                rememberLocalVote(key, now)
                SubmitVoteResult.Queued
            }
        }
    }

    override suspend fun getScore(key: BikeKey): BikeRatingScore {
        val empty = BikeRatingScore(key, positiveCount = 0, totalCount = 0)
        if (!RatingPaths.isValid(key)) return empty
        val snapshot = runCatching {
            withTimeout(WriteTimeoutMs) { database.reference(RatingPaths.scores(key)).get() }
        }.getOrNull() ?: return empty
        if (!snapshot.exists) return empty

        val recent = snapshot
            .child("months")
            .children
            .sortedByDescending { it.key.orEmpty() }
            .take(ScoreWindowMonths)
        return BikeRatingScore(
            key = key,
            positiveCount = recent.sumOf { it.child("positive").asCount() },
            totalCount = recent.sumOf { it.child("total").asCount() },
            issueCounts = snapshot
                .child("issues")
                .children
                .mapNotNull { issue ->
                    val name = issue.key ?: return@mapNotNull null
                    val known = BikeIssue.entries.firstOrNull { it.name == name }
                    known?.let { it to issue.asCount() }
                }.toMap(),
        )
    }

    override suspend fun flushPendingVotes(): PendingVoteFlushResult =
        flushMutex.withLock {
            val queued = pendingVotes.read()
            if (queued.isEmpty()) return PendingVoteFlushResult(0, 0, hasPendingVotes = false)
            var sent = 0
            var rejected = 0
            while (sent + rejected < queued.size) {
                when (send(queued[sent + rejected])) {
                    SendOutcome.Sent -> sent++
                    is SendOutcome.Rejected -> rejected++
                    SendOutcome.Unreachable -> break
                }
            }
            val resolved = sent + rejected
            pendingVotes.replaceWith(queued.drop(resolved))
            val result = PendingVoteFlushResult(sent, rejected, resolved < queued.size)
            logRatingDiagnostic("flush sent=${result.sentCount} rejected=${result.rejectedCount} pending=${result.hasPendingVotes}")
            return result
        }

    /**
     * Sends everything the queue is holding, oldest first, stopping at the first one that doesn't
     * land so the order riders cast them in is preserved.
     *
     * Rejected votes are dropped rather than retried — a vote the rules refuse will be refused
     * again, and leaving it at the head of the queue would block every vote behind it forever.
     */
    private suspend fun flushPending() = flushPendingVotes()

    private suspend fun send(vote: PendingVote): SendOutcome {
        val key = vote.key
        // Timed out the same as the write below: a stuck sign-in (seen on web, where the SDK
        // can hang here with no rejection at all) must not leave the rider's vote in limbo.
        val uid = runCatching { withTimeout(WriteTimeoutMs) { identity.currentId() } }
            .getOrElse { return SendOutcome.Unreachable }
        val voteId = database.reference(RatingPaths.votes(key)).push().key
            ?: return SendOutcome.Rejected
        val update = updateBuilder.build(
            key = key,
            uid = uid,
            voteId = voteId,
            isPositive = vote.isPositive,
            isElectric = vote.isElectric,
            issues = vote.issues,
            scopeSource = vote.scopeSource,
            castAtMs = vote.castAtMs,
        )
        return runCatching {
            withTimeout(WriteTimeoutMs) { database.reference().updateChildren(update) }
            SendOutcome.Sent
        }.getOrElse { error ->
            if (error.isPermissionDenied()) {
                SendOutcome.Rejected
            } else {
                SendOutcome.Unreachable
            }
        }
    }

    /**
     * A rules rejection is permanent and a dropped connection isn't, and the two have to be told
     * apart to choose between queueing the vote and giving up on it. The SDK surfaces both as a
     * plain database exception, so the message is all there is to go on — and erring towards
     * "unreachable" only costs a retry, while erring the other way loses a rider's vote.
     */
    private fun Throwable.isPermissionDenied(): Boolean = message?.contains("permission", ignoreCase = true) == true

    private fun DataSnapshot.asCount(): Int =
        when (val raw = value) {
            is Number -> raw.toInt()
            is String -> raw.toIntOrNull() ?: 0
            else -> 0
        }

    private fun lastLocalVoteAt(key: BikeKey): Long? = runCatching { store.read(cooldownKey(key))?.toLongOrNull() }.getOrNull()

    private fun rememberLocalVote(
        key: BikeKey,
        atMs: Long,
    ) {
        runCatching { store.write(cooldownKey(key), atMs.toString()) }
    }

    private fun cooldownKey(key: BikeKey) = cacheKey("vote_cooldown", key.networkId, key.bikeNumber)
}

private sealed interface SendOutcome {
    data object Sent : SendOutcome

    data object Unreachable : SendOutcome

    data object Rejected : SendOutcome
}
